import { DatePipe } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import { LOCALE, UI_DATE_FORMAT } from "@lib/constants/ui-formats";

import { AppDateAdapter, APP_DATE_FORMATS } from "@app/shared/date/app-date-adapter";

export interface DateChangeReason {
  id: string | number;
  label: string;
}

export interface DateChangeDialogData {
  title: string;
  originalDate?: string | Date;
  reasons: DateChangeReason[];
  newDateLabel: string;
  originalDateLabel?: string;
  reasonLabel?: string;
  commentsLabel?: string;
  commentsPlaceholder?: string;
  saveButtonText?: string;
  cancelButtonText?: string;
  dateErrorMessage?: string;
}

export interface DateChangeDialogResult {
  reasonId: string | number;
  newDate: Date;
  comments?: string;
}

@Component({
  selector: "app-date-change-dialog",
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
  templateUrl: "./date-change-dialog.component.html",
  styleUrl: "./date-change-dialog.component.scss",
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
  ],
})
export class DateChangeDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<DateChangeDialogComponent, DateChangeDialogResult | undefined>,
  );
  private readonly fb = inject(FormBuilder);
  readonly data = inject<DateChangeDialogData>(MAT_DIALOG_DATA);

  private readonly datePipe = new DatePipe(LOCALE);

  readonly form: FormGroup;
  readonly minDate = new Date();
  readonly isSaving = signal(false);

  readonly title = this.data.title;
  readonly originalDateLabel = this.data.originalDateLabel ?? "Original Date";
  readonly reasonLabel = this.data.reasonLabel ?? "Reason";
  readonly newDateLabel = this.data.newDateLabel;
  readonly commentsLabel = this.data.commentsLabel ?? "Comments (optional)";
  readonly commentsPlaceholder =
    this.data.commentsPlaceholder ?? "Leave a comment";
  readonly saveButtonText = this.data.saveButtonText ?? "Save";
  readonly cancelButtonText = this.data.cancelButtonText ?? "Cancel";
  readonly dateErrorMessage =
    this.data.dateErrorMessage ?? "Please select a valid future date";
  readonly reasons = this.data.reasons;

  constructor() {
    this.form = this.fb.group({
      reasonId: ["", Validators.required],
      newDate: [new Date(), Validators.required],
      comments: [""],
    });
  }

  ngOnInit(): void {
    this.minDate.setHours(0, 0, 0, 0);
  }

  get originalDateText(): string {
    const d = this.data.originalDate;
    if (!d) return "Not Set";
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "Not Set";
    return this.datePipe.transform(date, UI_DATE_FORMAT) ?? "Not Set";
  }

  save(): void {
    if (this.form.invalid || this.isSaving()) return;

    const formValue = this.form.value;
    const result: DateChangeDialogResult = {
      reasonId: formValue.reasonId,
      newDate: formValue.newDate,
      comments: formValue.comments || undefined,
    };

    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
